import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GA4_ID, META_PIXEL_ID,
  initGA4, initMetaPixel, trackPageView, trackScrollDepth,
} from '../lib/analytics';
import { captureUtmParams } from '../lib/utm';

// Milestones to fire scroll depth events (% of total page height)
const DEPTH_MILESTONES = [25, 50, 75, 90] as const;

export default function Analytics() {
  const { pathname } = useLocation();
  const initialized   = useRef(false);
  // Tracks which milestones have fired for the current page
  const fired         = useRef<Set<number>>(new Set());

  // -- One-time mount: init scripts + capture UTM params ----------------------
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    captureUtmParams();

    if (GA4_ID && GA4_ID !== 'G-XXXXXXXXXX' && !document.getElementById('ga4-script')) {
      const script = document.createElement('script');
      script.id    = 'ga4-script';
      script.async = true;
      script.src   = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(script);
      script.onload = initGA4;
    }

    if (META_PIXEL_ID) initMetaPixel();
  }, []);

  // -- Page view on every route change ----------------------------------------
  useEffect(() => {
    // Reset scroll milestones for new page
    fired.current.clear();

    const timer = setTimeout(() => {
      trackPageView(pathname, document.title);
    }, 50);
    return () => clearTimeout(timer);
  }, [pathname]);

  // -- Scroll depth tracking ---------------------------------------------------
  useEffect(() => {
    let ticking = false;

    function handleScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const scrollTop    = window.scrollY;
        const docHeight    = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        const pct = Math.round((scrollTop / docHeight) * 100);

        for (const milestone of DEPTH_MILESTONES) {
          if (pct >= milestone && !fired.current.has(milestone)) {
            fired.current.add(milestone);
            trackScrollDepth(milestone, pathname);
          }
        }
      });
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname]);

  return null;
}
